import { tool } from "ai";
import { parseModelDataUrl } from "playground/agents/model-media";
import { z } from "zod";

const description = `
Evaluates TypeScript/JavaScript in the browser REPL sandbox.
No wrapping is required, and if you want the result returned, do not include a semicolon:
  - If an error occurs, it will be visible in the console output
  - If the expression returns a result, it will be returned as the result
State persists across calls — variables and imports from previous executions are available in subsequent ones. Call \`clear()\` to reset the REPL context.

To run a script file instead of inline code, pass \`filePath\` (any path \`read\` accepts, e.g. a skill's \`scripts/\` file) and \`args\`. The script default-exports a handler that receives \`args\` (plus the injected \`projectId\`) as its one parameter (\`export default async function (args) { … }\`), and its return value is the result. Its own relative imports resolve from the project root, so it uses @/ for its imports.

## Environment

- Full TypeScript support (types are stripped automatically)
- TSX/JSX is Bear's JSX, not React's: \`<Wall name="W1" />\` evaluates to a bear element (unmaterialized: it has no size, pose or geometry yet). To inspect one, build it:

\`\`\`ts
import { materialize } from "bear/engine/materialize";
import { printTree } from "bear/engine/print";
import { d } from "bear/measurements";
import Wall from "bear/blueprints/Wall";

const model = await materialize(
  <Wall name="W1" path={[[0, 0], [d\`8ft\`, 0]]} height={d\`8ft\`} />,
);
printTree(model)   // or q(model).find(/^Stud/) for the nodes themselves
model.dispose()    // you called materialize, so you free its solids
\`\`\`
- ES module imports work. The \`bear\` and \`three\` packages are available directly, and so are \`zod\` and \`playground/records/format\` for record tables (see the \`records\` skill).
- To import project files, use the @/ prefix (e.g. \`import { helper } from "@/utils/helper"\`). Extension can be omitted. Relative (\`./\`, \`../\`) imports resolve from the project root here (the submitted code has no directory of its own), so prefer @/ — though a project file imported via @/ may use relative imports among its own siblings.
- For any other npm package, import from esm.sh: \`import confetti from "https://esm.sh/canvas-confetti"\`
- The DOM is available (this runs in a browser sandbox)
- In a task workspace, \`await workspace.present(["path/to/file.md", "inputs/plan.pdf"])\` replaces the artifact tabs the person sees. Repeat with the current minimal set as the task evolves. It does not edit or apply project files. The viewer keeps the person's current focus where possible. An empty list clears the artifact tabs.

## Your tools

Every eval has your own tools as globals. Each eval gets fresh ones, and they stop working when it returns, so don't keep them in REPL variables across calls.

- \`read\`, \`write\`, \`edit\` and \`bash\` take the same input as the top-level tools, for multi-step file work in one script: \`const { content } = await read({ path: "model/index.tsx" })\`. A \`write\` overwrites only a file this eval read in full and nobody changed since. What a script reads is not shown to you unless you return it, and doesn't count as read for a later top-level \`write\`.
- Everything else is a namespace in \`tools\`: \`bear\` for your operations (\`getDiagnostics\`, \`edit_sketch\`, \`measurementCalc\`, \`run\`, \`source\`, \`show\`, the checkpoint tools), and one per jig with tools (\`@local/framing-count\` is \`tools.local["framing-count"]\`). \`await tools.list()\` lists namespaces, \`await tools.list("bear")\` or \`await tools.search("diagnostics")\` gives exact selectors, and \`await tools.describe("bear.getDiagnostics")\` gives the typed signature. Describe a tool before its first use, then call it: \`const { diagnostics } = await tools.bear.getDiagnostics({ filename: "model/index.tsx" })\`. Every eval runs in the page, so tools listed with \`runtimes: ["page"]\` work here too.
- A call resolves to the tool's result and throws when the tool fails, with what came back as the error's \`cause\`. The console logs each call's outcome; if later code throws, earlier calls may have completed, so check state before retrying and don't replay the whole script.

\`\`\`ts
const { diagnostics: types } = await tools.bear.getDiagnostics({ filename: "model/index.tsx" });
const { errors, diagnostics } = await tools.bear.run({});
({ types, errors, diagnostics })   // diagnostics: what the built model got wrong
\`\`\`

## Built-in functions

- \`clear()\` — resets the REPL context, clearing all previously declared variables and imports.
- \`viewManifold(manifold, options?)\` — renders a Manifold object to a PNG and returns a base64 data URL. Use this to visually inspect Manifold geometry without building a full Bear model. Options: \`{ width?, length?, camera? }\` (same camera shape as view3dModel). Must be awaited.
- \`q(...)\`: queries the tree on screen, bound to the model on screen (the engine's \`bear/engine/query\` has no notion of a current model; \`q\` here is the host's binding of it). One rule: \`.find(sel)\` searches all descendants of the current set; every other method stays put. \`q()\` is the whole tree, \`q(node)\` scopes to a subtree, and \`q(Drywall)\` / \`q("Level-1")\` / \`q(/^Wall/)\` are sugar for \`q().find(sel)\` over the whole tree. Selectors: string = exact label (the \`name\` prop, or what \`printTree\` shows), RegExp = label pattern, PartDef or assembly component = identity (import it first, e.g. \`import Drywall from "bear/blueprints/materials/Drywall"\`; \`q(Wall)\` matches the nodes a \`<Wall>\` expanded into), or a predicate (keep predicates anonymous or camelCase; a capitalized function name is read as a component). \`.named()\` filters the current set by label without descending; AND = chain a filter after a search: \`q(NominalLumber).named(/^HDR/)\`. Layered selection is chaining: \`q("Level-1").find(/^Wall/).nth(2).find("Post").one()\`. Terminals are strict: \`.one()\` throws unless exactly one match, \`.first()\` throws on empty, \`.at(i)\` is the quiet nullable accessor. Graph hops walk relationship edges (declared with relation props: \`bearsOn\`/\`carries\`, \`fastenedTo\`, \`feeds\`/\`fedBy\`): \`.follow(kind)\` is one hop along edges of that kind (\`{ dir: "in" }\` goes against the direction), \`.trace(kind)\` is the transitive closure of the kind's transfer semantics (\`q("J-3").trace("bears")\` is the load path to the ground; \`q("P-1").trace("bears", { dir: "in" })\` is everything that pier carries). For \`bears\`, containment counts: an element with no declared support is carried by its enclosing assembly, so a coarse \`<Floor bearsOn={[wallA, wallB]}>\` edge and fine member edges compose into one path, and an ungrounded path ends at the tree root. and \`.edges(kind?)\` returns the edge records themselves so you can read their props (fastener schedules, flow demands). \`.system(name)\` filters to nodes inside a \`<System name>\` wrapper, whose descendants' edges carry \`edge.system\`. Pass the query itself to \`hide\`, \`isolate\`, \`shade\` and \`view3dModel\`: it does not need \`.all()\`. Query errors name the chain and diagnose the usual mistakes; read them before retrying.
- \`runTests()\`: built-in, runs the checks registered with \`bear/test\` against the model on screen, so it needs no source import and no second materialize, and the \`q\` each check receives is bound to the same tree as the one you have here. In a saved test file, import \`runTests\` from \`bear/test\` and pass it the model: \`runTests(Source)\`.
- \`bear/test\`: assertions over a model. \`it(name, fn)\` / \`describe\` register; \`await runTests()\` runs them and returns a report naming what passed and failed. Matchers are the Jest/Vitest set; see \`.agents/skills/bear-reference/references/bear/test.md\`.

\`\`\`ts
import { expect, it } from "bear/test";
import NominalLumber from "bear/blueprints/materials/NominalLumber";

it("has 14 joists", ({ q }) => expect(q(NominalLumber).length).toBe(14));
await runTests()   // the built-in: checks the model on screen
\`\`\`

### The user's 3D viewer vs. your renders

There are two separate views of the model. Nothing done to one shows up in the other.

- **The user's 3D viewer** is what the user is looking at. \`hide\`, \`isolate\`, \`shade\` and \`unshade\` change it, and only it. Use them to show the user something they asked to see, and leave the viewer as they'd want it when you're done. Don't use them to look at the model yourself.
- **Your renders** are \`view3dModel(options)\` (below). Each call materializes the model again into its own offscreen scene, set up only by its options: \`camera\`, \`slice\`, \`hide\`, \`isolate\`, \`shade\`. It never sees what you did to the user's viewer, so a \`shade()\` call does not color the next \`view3dModel()\`. Pass the same groups as its \`shade\` option instead.
- \`viewUser3dModel()\` returns a PNG of the user's 3D model view as they see it right now: their camera, hides, shades, section cut and selection. It is only the 3D view, not the rest of their screen. Use it to check what you changed in their view, or to see what they're pointing at. The user may not have the 3D model open; then it throws, and there is nothing of theirs to look at.

- \`hide(query?)\`: hides the nodes a query matches in the user's viewer, along with everything under them. With no argument it hides the whole model.
- \`isolate(query?)\`: leaves only the nodes a query matches visible in the user's viewer, hiding the rest. With no argument it shows the whole model again, which is how you undo a \`hide\`.
- \`shade(query, color, opacity?)\`: draws the nodes a query matches in the user's viewer in a flat color instead of their materials. Plain color words (\`red\`, \`orange\`, \`yellow\`, \`green\`, \`blue\`, \`purple\`, \`gray\`) map to a palette made to look good together on a model, so use them; a hex or any other CSS color is drawn exactly. \`opacity\` defaults to 1. Use your judgment with it: a model where every group is solid reads flat, while layers of transparency show what's inside and what matters. For example, the focus solid, supporting groups around 0.5, and context around 0.1. Use shading to point at things: changed members in one color, context grayed out in another. Where shades overlap, the last call wins. It only changes the view, never the model, and a rebuild clears it.
- \`unshade(query?)\`: gives the nodes a query matches their own materials back. With no argument it clears every shade.

\`\`\`ts
import NominalLumber from "bear/blueprints/materials/NominalLumber";
// Show the user: wash out everything, then make the headers stand out.
shade(q(), "gray", 0.1);
shade(q(NominalLumber).named(/^HDR/), "orange");
viewUser3dModel();           // check what the user now sees
unshade();                   // later, once the user is done with it

// Look for yourself: the same picture, without touching the user's viewer.
await view3dModel({ shade: [
  { nodes: q(), color: "gray", opacity: 0.1 },
  { nodes: q(NominalLumber).named(/^HDR/), color: "orange" },
] });
\`\`\`

## Tools
\`\`\`ts
type SliceDefinition = {
  /** Axis to slice along: "x", "y", or "z" */
  axis: "x" | "y" | "z";
  /** Position along the axis in project units. */
  position: number;
  /** Flip the slice direction to keep the other side (default: false) */
  flipped?: boolean;
};

/**
 * Where the camera sits relative to what it is looking at, as a compass point
 * or a raw vector. Axes are Z-up: +X east, +Y north, +Z up.
 */
type CameraDirection =
  | "iso-ne" | "iso-nw" | "iso-se" | "iso-sw"
  | "top" | "bottom"
  | "north" | "south" | "east" | "west"
  | [number, number, number];

type View3DModelOptions = {
  /** The width of the image in pixels */
  width?: number;
  /** The length of the image in pixels */
  length?: number;
  /** Show the ground beneath the model. Defaults to true. */
  showGround?: boolean;
  /**
   * Where to look from. The distance is always solved for you from the
   * bounding box of whatever is framed, so you never have to guess it.
   * Units are 32nds of an inch: multiply feet by 12 * 32.
   */
  camera?: {
    /** Viewing direction. Default "iso-sw". */
    direction?: CameraDirection;
    /**
     * Frame on one named element instead of the whole model, e.g.
     * "STAIR-RIM-L". Names come from the \`name\` prop. If the name is not
     * unique the error lists the full FQNs; pass one of those instead.
     */
    focus?: string;
    /** Aim point. Defaults to the centre of what is framed. */
    target?: [number, number, number];
    /** Framing slack: 1 fits tight, 2 pulls back. Default 1.2. */
    margin?: number;
    fov?: number;
    near?: number;
    far?: number;
    /** Absolute camera position. Prefer direction + focus: this turns auto-framing OFF. */
    position?: [number, number, number];
    /** Absolute aim point. Overrides target. */
    lookAt?: [number, number, number];
  };
  /** Show local axes on every object (useful for debugging transforms) */
  showObjectAxes?: boolean;
  /**
   * File path to materialize instead of the main source entry point.
   * Use a project path like "first-floor.tsx" or "@/first-floor".
   * The file must export a bear element or a function that returns one.
   * If omitted, materializes the model entry (model/index.tsx, or a root model.tsx).
   */
  entryPoint?: string;
  /** Named export to render from the entry point. Defaults to its default export. */
  exportName?: string;
  /** Props to pass when the selected export is a Bear component function. */
  props?: Record<string, unknown>;
  /**
   * Slice the model along an axis to cut away geometry that's in the way.
   *   import { d } from "bear/measurements";
   *   await view3dModel({ slice: { axis: "z", position: d\`4ft\` } })
   */
  slice?: SliceDefinition;
  /**
   * Hide the nodes a query matches, and everything under them. Framing then
   * solves against what is left, so this is how you shoot past clutter:
   *   await view3dModel({ hide: q(Drywall) })
   */
  hide?: BearQuery | TreeNode | Iterable<TreeNode>;
  /** Leave only the nodes a query matches in the shot; hide the rest. */
  isolate?: BearQuery | TreeNode | Iterable<TreeNode>;
  /**
   * Draw the nodes a query matches in a flat color; later entries win where
   * they overlap. Same rules as the \`shade\` built-in:
   *   await view3dModel({ shade: [
   *     { nodes: q(), color: "gray", opacity: 0.1 },
   *     { nodes: q("HDR-1"), color: "orange" },
   *   ] })
   */
  shade?: {
    nodes: BearQuery | TreeNode | Iterable<TreeNode>;
    color: string;
    opacity?: number;
  }[];
};
/**
 * Renders the 3D model and returns a screenshot as a base64 PNG.
 *
 * Framing is automatic. To change the view, say where to look from and what
 * to look at. Never solve for a distance by hand:
 *   await view3dModel({ camera: { direction: "iso-ne" } })
 *   await view3dModel({ camera: { direction: "east", focus: "STAIR-RIM-L" } })
 *
 * Alongside the image, the console output carries a framing line: what was
 * framed, its centre and size, and the camera position and distance. Read it
 * to correct the view in one step. If what you framed is hidden behind
 * something, change \`direction\` or cut with \`slice\`.
 *
 * By default materializes the model entry. Use the \`entryPoint\` option to materialize
 * a different file — useful for inspecting individual components:
 *   await view3dModel({ entryPoint: "first-floor.tsx" })
 *
 * If the source materializes successfully, the output image should be inspected to
 * validate the model matches the intention.
 *
 * In case of runtime error, debug by looking at the console output.
 *
 * @returns a base64 encoded png of the 3d model
 */
view3dModel = (options?: View3DModelOptions) => Promise<string>
// IMPORTANT: view3dModel is async — you must await it:
//   await view3dModel()
// If you forget to await, the result will be "[object Promise]" instead of an image.

type ViewManifoldOptions = {
  /** The width of the image in pixels */
  width?: number;
  /** The length of the image in pixels */
  length?: number;
  /** Show the ground beneath the solid. Defaults to true. */
  showGround?: boolean;
  /** A bare manifold has no named elements, so there is nothing to \`focus\` on. */
  camera?: Omit<NonNullable<View3DModelOptions["camera"]>, "focus">;
};
/**
 * Renders a standalone Manifold object to an image.
 * Use this to visually inspect Manifold geometry in the REPL
 * without building a full Bear model.
 *
 * @returns a base64 encoded png of the manifold
 */
viewManifold = (manifold: Manifold, options?: ViewManifoldOptions) => Promise<string>
// IMPORTANT: viewManifold is async — you must await it:
//   const M = await initManifold();
//   const { cube, cylinder } = M.Manifold;
//   const box = cube([384, 384, 384], true);
//   const hole = cylinder(500, 100, 100, 36, true);
//   const shape = box.subtract(hole);
//   await viewManifold(shape)
//   // Manifolds made outside materialize are yours to free:
//   for (const m of [box, hole, shape]) m.delete();
\`;`;

/** What a non-image result reads as. A data URL landing here failed to parse
 *  as usable media, and dumping its bytes as prose helps nobody. */
const resultText = (result: unknown): string =>
  typeof result === "string" && result.startsWith("data:")
    ? "[image dropped: the stored copy was truncated. Run view3dModel again to see it.]"
    : String(result ?? "");

/** Exactly one of `code` and `filePath`; the browser enforces it. */
export const evalInputSchema = z.object({
  code: z.string().optional().describe("The TypeScript/JavaScript to execute"),
  filePath: z
    .string()
    .optional()
    .describe(
      "A script to run instead of code, e.g. '.agents/skills/texture-acquisition/scripts/download-texture.ts'",
    ),
  args: z
    .record(z.string(), z.unknown())
    .optional()
    .describe(
      "With filePath: the script handler's args. projectId is injected.",
    ),
});

const evalTool = tool({
  description,
  inputSchema: evalInputSchema,
  outputSchema: z.union([
    z.object({
      result: z.any().describe("What was returned by the JS expression"),
      console: z.string().describe("Console output during execution."),
    }),
    z.object({
      error: z
        .string()
        .describe("What the code threw, followed by its console output."),
    }),
  ]),
  toModelOutput: ({ output }) => {
    // A compaction pass replaces the whole output with a placeholder STRING,
    // so the two fields are read off it rather than destructured: `{}.result`
    // silently projected an empty text part where the placeholder should be.
    if (typeof output === "object" && output !== null && "error" in output) {
      return { type: "text" as const, value: `Error: ${output.error}` };
    }
    const { result, console: consoleOutput } =
      typeof output === "object" && output !== null
        ? output
        : { result: output, console: "" };
    const parts: Array<
      | { type: "text"; text: string }
      | { type: "image-data"; data: string; mediaType: string }
    > = [];

    // Shape is not evidence: a screenshot truncated to fit storage still reads
    // as a data URL, and replaying its base64 fails the whole request. Every
    // later turn too, since the prompt is rebuilt from what is stored.
    const image = parseModelDataUrl(result);

    if (image) {
      parts.push({ type: "image-data", ...image });
    } else {
      parts.push({ type: "text", text: resultText(result) });
    }

    if (consoleOutput?.trim()) {
      parts.push({
        type: "text",
        text: `Console output:\n${consoleOutput}`,
      });
    }
    return {
      type: "content" as const,
      value: parts,
    };
  },
});

export default evalTool;
